import React, { useEffect, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  createEmailTemplate,
  getEmailTemplateList,
  updateEmailTemplate,
  viewEmailTemplate,
} from "../../services/EmailService.js/emailService";
import { AlertTriangleIcon, Check, Plus } from "lucide-react";
import FormHeading from "../../components/formHeader";

/* ================= FONT WHITELIST ================= */
const Font = Quill.import("formats/font");
Font.whitelist = [
  "arial",
  "roboto",
  "open-sans",
  "poppins",
  "montserrat",
  "lato",
  "times-new-roman",
  "georgia",
  "merriweather",
  "playfair",
  "courier-new",
  "fira-code",
  "oswald",
  "bebas",
  "pacifico",
];
Quill.register(Font, true);

/* ================= TOOLBAR ================= */
const modules = {
  toolbar: [
    [{ font: Font.whitelist }],
    [{ size: ["small", false, "large", "huge"] }],
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "image", "code-block"],
    ["clean"],
  ],
};

export default function TemplateMaster() {
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(true);

  const [name, setName] = useState(null);
  const [subject, setSubject] = useState(null);
  const [creationResponse, setCreationResponse] = useState(null);
  const [updateResponse, setUpdateResponse] = useState(null);
  const [inputError, setInputError] = useState(null);
  const [templateList, setTemplateList] = useState(null);
  const [templateType, setTemplateType] = useState("create");
  const [viewResponse, setViewResponse] = useState(null);
  const [id, setId] = useState(null);

  const handleView = async (id) => {
    try {
      setTemplateType("update");
      const res = await viewEmailTemplate({ id });

      if (res?.data) {
        setId(res?.data?.id);
        setName(res?.data?.name);
        setSubject(res?.data?.subject);
        setMessage(res?.data?.body_html);
        setStatus(res?.data?.is_active ? true : false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleReset = () => {
    setTemplateType("create");
    setName("");
    setSubject("");
    setInputError("");
    setMessage("");
    setCreationResponse(null);
    setViewResponse(null);
    setUpdateResponse(null);
  };

  const handleCreateTemplate = async () => {
    if (!name) {
      return setInputError("Name is required!!");
    } else if (!subject) {
      return setInputError("Subject Field can't be empty");
    } else if (message === "") {
      return setInputError("Message Field can't be empty");
    } else if (!status) {
      return setInputError("Please toggle status on");
    }

    setInputError(null);
    try {
      const res = await createEmailTemplate({
        name,
        subject,
        is_active: status,
        body_html: message,
      });

      if (res) {
        setCreationResponse(res);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getList = async () => {
    try {
      const res = await getEmailTemplateList();

      if (res) {
        setTemplateList(res);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdate = async () => {
    if (!id) return;
    if (!name) {
      return setInputError("Name is required!!");
    } else if (!subject) {
      return setInputError("Subject Field can't be empty");
    } else if (message === "") {
      return setInputError("Message Field can't be empty");
    } else if (!status) {
      return setInputError("Please toggle status on");
    }

    const res = await updateEmailTemplate({
      id,
      name,
      subject,
      is_active: status,
      body_html: message,
    });

    if (res) {
      setUpdateResponse(res);
    }
  };

  useEffect(() => {
    getList();
  }, []);

  /* ============ LOAD GOOGLE FONTS DYNAMICALLY ============ */
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Roboto&family=Open+Sans&family=Poppins&family=Montserrat&family=Lato&family=Merriweather&family=Playfair+Display&family=Fira+Code&family=Oswald&family=Bebas+Neue&family=Pacifico&display=swap";
    document.head.appendChild(link);
  }, []);

  return (
    <div className="min-h-screen mt-12 bg-slate-100 ">
      {/* ================= FONT CSS ================= */}
      <style>{`
        .ql-font-arial { font-family: Arial, sans-serif; }
        .ql-font-roboto { font-family: 'Roboto', sans-serif; }
        .ql-font-open-sans { font-family: 'Open Sans', sans-serif; }
        .ql-font-poppins { font-family: 'Poppins', sans-serif; }
        .ql-font-montserrat { font-family: 'Montserrat', sans-serif; }
        .ql-font-lato { font-family: 'Lato', sans-serif; }

        .ql-font-times-new-roman { font-family: "Times New Roman", serif; }
        .ql-font-georgia { font-family: Georgia, serif; }
        .ql-font-merriweather { font-family: 'Merriweather', serif; }
        .ql-font-playfair { font-family: 'Playfair Display', serif; }

        .ql-font-courier-new { font-family: "Courier New", monospace; }
        .ql-font-fira-code { font-family: 'Fira Code', monospace; }

        .ql-font-oswald { font-family: 'Oswald', sans-serif; }
        .ql-font-bebas { font-family: 'Bebas Neue', sans-serif; }
        .ql-font-pacifico { font-family: 'Pacifico', cursive; }

        .ql-container {
          min-height: 230px;
          font-size: 15px;
        }
      `}</style>

      <div className="w-full mx-auto bg-white  shadow-xl overflow-hidden">
        {/* ================= HEADER ================= */}
        <FormHeading
            subtitle={
              templateType === "create"
                ? "Add a new template for email"
                : "Update email template"
            }
            title={templateType === "create" ? "Create New Template" : "Edit Template"}
            formType={templateType}
          />

        {/* create template button */}
        {(creationResponse?.status?.code === 200 ||
          updateResponse?.status?.code === 200) &&
          templateType !== "create" && (
            <button
              onClick={handleReset}
              className="rounded-xl my-4 ml-4 w-fit hover:from-gray-800 hover:to-gray-900 active:from-slate-600  bg-gradient-to-r flex items-center gap-2 from-indigo-600 via-blue-600 to-cyan-500 p-4 text-white shadow-lg"
            >
              <Plus />
              Create New
            </button>
          )}

        {/* show response message */}

        {(creationResponse?.status?.code === 200 ||
          updateResponse?.status?.code === 200) && (
          <div className="flex items-center gap-4 px-6 py-4 my-4 ml-4 rounded-2xl bg-green-50 border-2 border-green-300 text-green-800 shadow-md">
            <Check className="w-6 h-6 text-green-700 animate-bounce" />
            <p className="text-base font-semibold">
              {creationResponse?.status?.code === 200
                ? "Email Template created successfully"
                : "Email Template updated successfully"}
            </p>
          </div>
        )}

        {creationResponse && creationResponse?.status?.code !== 200 && (
          <div className="flex items-center gap-4 px-6 py-4 my-4 ml-4 rounded-2xl bg-red-50 border-2 border-red-300 text-red-800 shadow-md">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-red-200 text-red-700 font-bold text-sm">
              !
            </span>
            <p className="text-base font-semibold">Configuration failed</p>
          </div>
        )}

        {/* ================= FORM ================= */}
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {templateType === "update" && (
              <div>
                <label className="text-sm font-medium">Id</label>
                <input
                  required
                  readOnly
                  value={id}
                  onChange={(e) => setId(e.target.value)}
                  className="mt-1 w-full rounded-lg px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:outline-none"
                  type="text"
                />
              </div>
            )}
            <div>
              <label className="text-sm font-medium">
                Template Name <span className="text-red-500">*</span>
              </label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-lg px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:outline-none"
                type="text"
              />
            </div>

            <div>
              <label className="text-sm font-medium">
                Email Subject <span className="text-red-500">*</span>
              </label>
              <input
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1 w-full rounded-lg px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:outline-none"
                type="text"
              />
            </div>
          </div>

          {/* ================= EDITOR ================= */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Message <span className="text-red-500">*</span>
            </label>

            <ReactQuill
              value={message}
              onChange={setMessage}
                            style={{ height: "280px", padding: "10px", borderRadius: "10px" }}

              modules={modules}
              theme="snow"
              placeholder="Write your email template here..."
            />
          </div>

          {/* ================= STATUS ================= */}
          <div className="flex items-center pt-10 justify-between">
           <div className="flex items-center gap-4"> <span className="text-sm font-medium">Status</span>
            <label className="relative inline-flex cursor-pointer">
              <input
                type="checkbox"
                checked={status}
                onChange={() => setStatus(!status)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-300 peer-checked:bg-blue-600 rounded-full"></div>
              <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition peer-checked:translate-x-5"></div>
            </label>
            <span className="text-sm text-gray-600">
              {status ? "Active" : "Inactive"}
            </span>
</div>

             {/* ================= SAVE ================= */}
          <div className="text-right">
            <button
              type="button"
              onClick={
                templateType === "create" ? handleCreateTemplate : handleUpdate
              }
              className={`px-6 py-2 ${templateType === "create" ? "bg-blue-600 text-white": 'bg-amber-500 text-black'}  rounded-lg hover:bg-blue-800 transition`}
            >
              {templateType === "create" ? "Save Template" : "Update Template"}
            </button>
          </div>
          </div>

          {inputError && (
            <div className="w-full flex items-center justify-center gap-3">
              <div className=" flex items-center justify-center gap-3 text-xl text-blue-700 font-bold">
                <AlertTriangleIcon />
                {inputError}
              </div>
            </div>
          )}
         
        </div>

        {/* ================= TABLE ================= */}
        {/* ================= TABLE ================= */}
        <div className="border-t p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">
            Template List
          </h2>

          {/* table wrapper for mobile scroll */}
          <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full text-sm bg-white border border-gray-200 rounded-xl overflow-hidden">
              {/* HEAD */}
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-5 py-3 text-left font-semibold">
                    Template Name
                  </th>
                  <th className="px-5 py-3 text-left font-semibold">Subject</th>
                  <th className="px-5 py-3 text-left font-semibold">Status</th>
                  <th className="px-5 py-3 text-center font-semibold">
                    Action
                  </th>
                  <th className="px-5 py-3 text-center font-semibold">
                    Created At
                  </th>
                </tr>
              </thead>

              {/* BODY */}
              <tbody className="divide-y divide-gray-100">
                {templateList?.data?.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition">
                    <td className="px-5 py-4 font-medium text-gray-800">
                      {item.name}
                    </td>

                    <td className="px-5 py-4 text-gray-600">{item.subject}</td>

                    <td className="px-5 py-4">
                      {item.is_active === 1 ? (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                          Active
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          Inactive
                        </span>
                      )}
                    </td>

                    <td className="px-5 py-4 text-center">
                      <button
                        type="button"
                        onClick={() => handleView(item.id)}
                        className="px-4 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-purple-700 to-blue-500 border border-blue-200 rounded-lg hover:bg-blue-50 transition"
                      >
                        Edit
                      </button>
                    </td>

                    <td className="px-5 py-4 text-center text-gray-500">
                      {new Date(item.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
